// Foundations
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

// rxjs
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  take,
} from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

// Services
import { AnalyticsService } from '@app/services/analytics.service';
import { AuthService } from '@app/auth/auth.service';
import { GetPetsWithStatusGQL, Pet } from '@app/services/graphql.service';
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';

// Components
import { ReservationPetInfo } from '@app/models/reservation-pet-info.model';

// Pipes
import { UnitConversionPipe } from '@app/pipes/unit-conversion.pipe';

// Models (additional criteria)
import { PetStatusType } from '@app/models/pet/pet-status.model';
import { ReservationRequestItem } from '@app/models/reservation/reservation-request.model';

@Component({
  selector: 'app-reservation-dogs',
  templateUrl: './reservation-dogs.component.html',
  styleUrls: ['./reservation-dogs.component.scss'],
})
export class ReservationDogsComponent implements OnInit, OnDestroy {
  // #region -> Property - IO <-
  // ----------

  @Input() needToReload$: Observable<boolean>;
  needToReload = false;
  needToReloadSubscription: Subscription;

  @Output() selectedPets = new EventEmitter<ReservationPetInfo[]>();
  @Output() navigateToNext = new EventEmitter();

  // ----------
  // #endregion

  /////////////////////////////

  petList: Pet[] = [];
  petTileList$: Observable<Pet[]>;

  isErrorOnSave = false;

  form: FormGroup;
  get petFormArray(): FormArray {
    return this.form.get('petArr') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private _analytics: AnalyticsService,
    private changeDetectorRef: ChangeDetectorRef,
    private authService: AuthService,
    private getPetsWithStatusQuery: GetPetsWithStatusGQL,
    private loadingIndicatorService: LoadingIndicatorService,
    private convertUnit: UnitConversionPipe
  ) {
    //
    this._initForm();
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.needToReloadSubscription = this.needToReload$.subscribe(
      (needToReloadFlag) => {
        this.needToReload = needToReloadFlag;
        if (needToReloadFlag) {
          this.fetchData();
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.needToReloadSubscription.unsubscribe();
  }

  // Public methods
  // ------------------------------------------------------------------------------------

  onSave() {
    this.selectedPets.emit(this.getSelectedPets());
    // this.navigateToNext.emit();
  }

  get isSaveEnabled() {
    // check if at least one pet is selected
    let isAtLeastOnePetSelected = false;
    this.petFormArray.controls.forEach((fg: FormGroup) => {
      if (fg.controls.isPetSelected.value) {
        isAtLeastOnePetSelected = true;
      }
    });

    return isAtLeastOnePetSelected;
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private getSelectedPets(): ReservationPetInfo[] {
    const pets = [];
    this.petFormArray.controls.forEach((fg: FormGroup) => {
      if (fg.controls.isPetSelected.value) {
        pets.push(
          this.petList.filter((pet) => pet.id === fg.controls.petId.value)[0]
        );
      }
    });
    return pets;
  }

  private createPet(petId, isPetSelected) {
    return this.fb.group({
      petId: [petId],
      name: [''],
      breedArr: this.fb.array([]),
      weight: [''],
      isPetSelected: [isPetSelected],
      agreementArr: this.fb.array([]),
      petDetails: [''],
    });
  }

  private addPet(petId, isPetSelected = false) {
    this.petFormArray.push(this.createPet(petId, isPetSelected));
  }

  resetFormData(reservationRequest?: ReservationRequestItem): void {
    //
    this._initForm();

    this.petList.forEach((pet) => {
      //
      if (reservationRequest) {
        this.addPet(pet.id, reservationRequest.isPetSelected(pet.id));
      } else {
        this.addPet(pet.id);
      }
    });
  }

  private _initForm(): void {
    //
    this.form = this.fb.group({
      petArr: this.fb.array([]),
    });
  }

  public fetchData(reservationRequest?: ReservationRequestItem): void {
    //
    this._initForm();

    //
    this.loadingIndicatorService.show();
    this.petList = [];
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.petTileList$ = this.getPetsWithStatusQuery
          .fetch({ accountId })
          .pipe(
            map((res) => {
              return res.data.getPetsWithStatus
                .filter(
                  (pet) =>
                    pet.active &&
                    pet.statusId === PetStatusType.PASSED_INTERVIEW /* &&
                    pet.vets?.length &&
                    pet.vaccines?.some(
                      (vaccine) =>
                        new Date().getTime() <
                        new Date(vaccine.expiresOn).getTime()
                    )*/
                )
                .map((pet) => {
                  const {
                    id,
                    firstName,
                    lastName,
                    nickName,
                    weight,
                    breeds,
                    vaccines,
                  } = pet;

                  // Add item to the form.
                  if (reservationRequest) {
                    this.addPet(
                      pet.id,
                      reservationRequest.isPetSelected(pet.id)
                    );
                  } else {
                    this.addPet(pet.id);
                  }

                  if (weight && weight.weight) {
                    weight.weight = this.convertUnit.transform(
                      weight.weight,
                      'g',
                      'lb'
                    );
                  }
                  const newPet = {
                    id,
                    firstName,
                    lastName,
                    nickName,
                    weight,
                    breeds,
                    vaccines,
                  };
                  this.petList.push(newPet);
                  return newPet;
                });
            }),
            finalize(() => {
              this.loadingIndicatorService.hide();

              // Trigger change detection when mutated.
              this.changeDetectorRef.markForCheck();
            })
          );
      });
  }
}
